import React, { useEffect, useRef } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
}

interface MousePosition {
  x: number;
  y: number;
}

const ParticleSystem: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef<MousePosition>({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Устанавливаем размер canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Обработчик движения мыши
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Создаем частицы
    const createParticle = (x: number, y: number): Particle => {
      const colors = [
        'rgba(59, 130, 246, 0.9)', // Синий - еще более контрастный
        'rgba(147, 51, 234, 0.9)', // Фиолетовый - еще более контрастный
        'rgba(34, 197, 94, 0.9)',  // Зеленый - еще более контрастный
        'rgba(239, 68, 68, 0.9)',  // Красный - еще более контрастный
        'rgba(245, 158, 11, 0.9)'  // Оранжевый - еще более контрастный
      ];

      return {
        id: Date.now() + Math.random(),
        x,
        y,
        vx: (Math.random() - 0.5) * 1.2, // Увеличил начальную скорость
        vy: (Math.random() - 0.5) * 1.2,
        size: Math.random() * 4 + 1.5, // Еще больше размер
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1,
        maxLife: Math.random() * 0.5 + 0.5
      };
    };

    // Инициализируем частицы
    const initParticles = () => {
      const particles: Particle[] = [];
      const particleCount = Math.min(Math.floor(window.innerWidth / 50), 35); // Еще больше частиц

      for (let i = 0; i < particleCount; i++) {
        particles.push(createParticle(
          Math.random() * canvas.width,
          Math.random() * canvas.height
        ));
      }

      return particles;
    };

    particlesRef.current = initParticles();

    // Функция анимации
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Обновляем и отрисовываем частицы
      particlesRef.current.forEach((particle) => {
        // Притяжение к мыши
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 120 && distance > 0) { // Уменьшил радиус притяжения
          const force = (120 - distance) / 120;
          particle.vx += (dx / distance) * force * 0.08; // Увеличил силу притяжения
          particle.vy += (dy / distance) * force * 0.08;
        } else {
          // Естественное активное движение, когда мышь далеко
          // Добавляем более частые случайные изменения направления
          if (Math.random() < 0.03) { // 3% шанс изменения направления
            particle.vx += (Math.random() - 0.5) * 0.4;
            particle.vy += (Math.random() - 0.5) * 0.4;
          }
          
          // Периодические "толчки" для более активного движения
          if (Math.random() < 0.005) { // 0.5% шанс сильного толчка
            particle.vx += (Math.random() - 0.5) * 0.8;
            particle.vy += (Math.random() - 0.5) * 0.8;
          }
        }

        // Обновляем позицию
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Затухание скорости - менее сильное для более активного движения
        particle.vx *= 0.995;
        particle.vy *= 0.995;

        // Границы экрана - более активный отскок
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.vx *= -0.9; // Меньше потери энергии при отскоке
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.vy *= -0.9;
        }

        // Ограничиваем позицию
        particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        particle.y = Math.max(0, Math.min(canvas.height, particle.y));

        // Обновляем жизнь (убираем перезапуск)
        particle.life = Math.max(0.3, particle.life - 0.001); // Замедлил уменьшение жизни

        // Рисуем частицу
        ctx.save();
        ctx.globalAlpha = particle.life;
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();
      });

      // Рисуем соединительные линии - более заметные
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.08)'; // Увеличил прозрачность
      ctx.lineWidth = 0.8; // Увеличил толщину

      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const p1 = particlesRef.current[i];
          const p2 = particlesRef.current[j];
          const distance = Math.sqrt(
            Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2)
          );

          if (distance < 100) {
            ctx.globalAlpha = (1 - distance / 100) * 0.3;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ background: 'transparent', zIndex: 1 }}
    />
  );
};

export default ParticleSystem; 